import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import { isValidEmailFormat } from '@shared/isValidEmailFormat';
import {
  selectSessionAdvisor,
  selectAdvisorHierarchyState,
  selectHierarchyInvitationMutationStatus,
} from '@client/model/selectors';
import {
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
  postAdvisorHierarchyInvitationByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';

/**
 * Hook for managing advisor hierarchy invitations
 *
 * This hook handles the complete invitation flow for adding new advisors to the team:
 * - Validates email addresses and checks for duplicates
 * - Manages invitation dialog state
 * - Dispatches invitation requests and tracks mutation status
 * - Shows success/error toast notifications
 * - Auto-refetches relationships on successful invitation
 *
 * Note: This hook is specifically for NEW invitations. For resending existing
 * invitations, use the useAdvisorHierarchyResend hook.
 */
export function useAdvisorHierarchyInvitation() {
  const dispatch = useDispatch();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { relationships, relationshipsFetchStatus } = useSelector(selectAdvisorHierarchyState);
  const { isMutating, error: mutationError } = useSelector(selectHierarchyInvitationMutationStatus);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const { toast } = useToast();
  const prevMutatingRef = useRef(false);

  // Fetch relationships on mount
  useEffect(() => {
    if (sessionAdvisor?.advisorId && relationshipsFetchStatus === 'idle') {
      dispatch(
        fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({
          advisorId: sessionAdvisor.advisorId,
        }),
      );
    }
  }, [dispatch, sessionAdvisor?.advisorId, relationshipsFetchStatus]);

  // Handle mutation completion
  useEffect(() => {
    // Mutation just completed (was mutating, now not)
    if (prevMutatingRef.current && !isMutating) {
      if (mutationError) {
        toast({
          title: 'Error',
          description:
            'There was an error sending the invitation. Please try again or contact support if the issue persists.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else {
        // Success - show toast and close dialog
        toast({
          title: 'Invitation Sent',
          description: 'The invitation has been sent successfully.',
          className: '-translate-y-5',
        });
        setDialogOpen(false);
        setEmailError(null);
      }
    }

    prevMutatingRef.current = isMutating;
  }, [isMutating, mutationError, toast, sessionAdvisor?.advisorId, dispatch]);

  /**
   * Opens the invitation dialog and clears any previous errors
   */
  const openDialog = () => {
    setDialogOpen(true);
    setEmailError(null);
  };

  /**
   * Closes the invitation dialog and clears any errors
   */
  const closeDialog = () => {
    setDialogOpen(false);
    setEmailError(null);
  };

  /**
   * Clears email validation error when user types
   * @param email - The current email input value
   */
  const handleEmailChange = (email: string) => {
    // Clear error when user types
    if (emailError) {
      setEmailError(null);
    }
  };

  /**
   * Validates email address and checks for duplicates
   * @param email - Email address to validate
   * @returns true if email is valid and not a duplicate, false otherwise
   */
  const validateEmail = (email: string): boolean => {
    const normalizedEmail = email.toLowerCase().trim();

    // Check if email format is valid
    if (!isValidEmailFormat(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }

    // Check if email already exists in relationships
    const existingRelationship = relationships?.find(
      relationship =>
        relationship.advisorEmail?.toLowerCase().trim() === normalizedEmail &&
        relationship.relationshipStatus !== 'inactive' &&
        relationship.relationshipStatus !== 'declined',
    );

    if (existingRelationship) {
      setEmailError('This advisor is already part of your team or has a pending invitation.');
      return false;
    }

    // Check if trying to invite themselves
    if (sessionAdvisor?.advisorEmail?.toLowerCase().trim() === normalizedEmail) {
      setEmailError('You cannot invite yourself to your own team.');
      return false;
    }

    // Clear error if validation passes
    setEmailError(null);
    return true;
  };

  /**
   * Sends a new invitation to an advisor
   * @param email - Email address of the advisor to invite
   * @param enabledClientAccess - Whether to grant client access to the invited advisor
   */
  const handleSendInvitation = (email: string, enabledClientAccess: boolean) => {
    const normalizedEmail = email.toLowerCase().trim();

    if (!sessionAdvisor?.advisorId) {
      setEmailError('Unable to send invitation. Please try again.');
      return;
    }

    // Dispatch actual invitation request
    dispatch(
      postAdvisorHierarchyInvitationByAdvisorIdRequest({
        advisorId: sessionAdvisor.advisorId,
        advisorEmail: normalizedEmail,
        isPreview: false,
        enabledClientAccess,
      }),
    );
  };

  return {
    // Data
    relationships,

    // Status
    isMutating,
    mutationError,

    // Dialog state
    dialogOpen,
    emailError,

    // Actions
    openDialog,
    closeDialog,
    handleEmailChange,
    validateEmail,
    handleSendInvitation,
  };
}

export type UseAdvisorHierarchyInvitationReturn = ReturnType<typeof useAdvisorHierarchyInvitation>;
